import {FC} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'

import {ITemplateProps} from 'src/types'
import {camelCaseToString, getStringValue, getWordsFromNumber} from 'src/utils'

import s from './PaystubTemplate1.module.scss'
import PaystubAdviceInfo from '../TemplateComponents/PaystubAdviceInfo'
import PaystubBaseMetrics from '../TemplateComponents/PaystubBaseMetrics'
import PreviewWaterMark from '../TemplateComponents/PreviewWaterMark'
import {getHandledTemplatesData} from '../utils'
const cx = cn.bind(s)

const PaystubTemplate1: FC<ITemplateProps<docs.IPaystubData>> = ({
	isPreviewOnly = true,
	hasFrame = false,
	activeItem = 0,
	data,
	className,
}) => {
	const {earingsData, deductionsData, customDeductions, payPeriodFrom, payPeriodTo, payDate} =
		getHandledTemplatesData(data, activeItem, true)

	return (
		<div
			className={cx('pst1', className, {
				'pst-preview-mode': isPreviewOnly,
				'doc-preview-frame': hasFrame,
			})}>
			{isPreviewOnly && <PreviewWaterMark className="pst1__watermark" />}
			<main className={cx('pst1__main', 'main')}>
				<div className={cx('main-left')}>
					<div className={cx('main-left__spacing', 'main-left-top')}>
						<div className={cx('main-left-top__codes', 'codes')}>
							<table>
								<thead className={cx('codes__head')}>
									<tr>
										<th>CO.</th>
										<th>File</th>
										<th>Dept.</th>
										<th>Clock</th>
										<th>Vchr NO.</th>
										<th>062</th>
									</tr>
								</thead>
								<tbody className={cx('codes__body')}>
									<tr>
										<td>{data?.coNumber}</td>
										<td>{data?.fileNumber}</td>
										<td>{data?.deptNumber}</td>
										<td>{data?.clockNumber}</td>
										<td>{data?.vchrNumber}</td>
										<td></td>
									</tr>
								</tbody>
							</table>
						</div>
						<PaystubBaseMetrics
							name={data?.companyInfo.employerName}
							telephone={data?.companyInfo.employerTelephone || ''}
							address1={data?.companyInfo.streetAddress1}
							address2={data?.companyInfo.streetAddress2 || ''}
							city={data?.companyInfo.city}
							state={data?.companyInfo.state}
							zipCode={data?.companyInfo.zipCode}
							className={cx('main-left-top__company-about')}
						/>
						<table className={cx('main-left-top__status')}>
							<tbody>
								{data?.employeeInfo.employeeSocial && (
									<tr>
										<th>Social Security Number:</th>
										<td>{data?.employeeInfo.employeeSocial}</td>
									</tr>
								)}
								<tr>
									<th>Taxable Marital Status:</th>
									<td>{data?.employeeInfo.maritalStatus}</td>
								</tr>
								<tr>
									<th>Dependents amount:</th>
									<td>{data?.employeeInfo.dependentsAmount}</td>
								</tr>
							</tbody>
						</table>
					</div>

					<table className={cx('main-left__payments', 'payments')}>
						<thead className={cx('payments__head', 'payments-thead')}>
							<tr>
								<th className={cx('payments-thead__item', 'ta-left')}>Earnings</th>
								<th
									className={cx(
										'payments-thead__item',
										data?.paymentType == 'salary' ? 'payments-thead__item--salary' : '',
									)}>
									Rate
								</th>
								{data?.paymentType !== 'salary' && (
									<th className={cx('payments-thead__item')}>Hours</th>
								)}
								<th className={cx('payments-thead__item')}>Amount</th>
								<th className={cx('payments-thead__item--empty')}></th>
								<th className={cx('payments-thead__item', 'pe-0')}>Year To Date</th>
							</tr>
						</thead>
						<tbody className={cx('payments__body', 'payments-tbody')}>
							{earingsData.map((item) => {
								if (!item) return null
								if (item.ytd === '' || item.ytd === '0.00') return null
								if (data?.paymentType !== 'salary' && item.key === 'salary') return null
								if (data?.paymentType !== 'hourly' && item.key === 'regular') return null
								if (data?.employmentType === 'employee' && item.key === 'contractor') return null
								if (data?.employmentType === 'contractor' && item.key === 'regular') return null
								return (
									<tr key={item.id}>
										<td className={cx('payments-tbody__item', 'ta-left')}>
											{camelCaseToString(item.name, 'upperFirst')}
										</td>
										<td className={cx('payments-tbody__item', 'pe-1')}>{item.rate}</td>
										{data?.paymentType !== 'salary' && (
											<td className={cx('payments-tbody__item')}>
												{item.key === 'tips' ? '1.00' : item.hours}
											</td>
										)}
										<td className={cx('payments-tbody__item')}>{item.total}</td>
										<td></td>
										<td className={cx('payments-tbody__item')}>{item.ytd}</td>
									</tr>
								)
							})}
							<tr>
								<td></td>
								<td className={cx('payments-tbody__item--accent', 'ta-left', 'ps-1')}>Gross Pay</td>
								{data?.paymentType !== 'salary' && (
									<td className={cx('payments-tbody__item--accent')}></td>
								)}

								<td className={cx('payments-tbody__item--accent')}>
									{'$'}
									{data?.items[activeItem].grossPayTotal}
								</td>
								<td></td>
								<td className={cx('payments-tbody__item')}>
									{data?.items[activeItem].grossPayYTDTotal}
								</td>
							</tr>
						</tbody>
					</table>

					<table className={cx('main-left__deduction', 'deduction')}>
						<thead className={cx('deduction__head', 'deduction-thead')}>
							<tr>
								<th className={cx('deduction-thead__item')}>Deduction</th>
								<th className={cx('deduction-thead__item')}>Statutory</th>
								<th className={cx('deduction-thead__item')}></th>
								<th className={cx('deduction-thead__item--empty')}></th>
								<th></th>
							</tr>
						</thead>
						<tbody className={cx('deduction__body', 'deduction-tbody')}>
							{deductionsData.map((item) => {
								if (!item) return null
								if (
									(item?.ytd === '' || item.ytd === '0.00') &&
									item.key !== 'medicare' &&
									item.key !== 'fica' &&
									item.key !== 'state' &&
									item.key !== 'federal'
								)
									return null
								return (
									<tr key={item.name}>
										<td></td>
										<td className={cx('deduction-tbody__item')}>{item.name}</td>
										<td className={cx('deduction-tbody__item', 'ta-right')}>
											- {getStringValue(item.total)}
										</td>
										<td></td>
										<td className={cx('deduction-tbody__item', 'ta-right')}>{item.ytd}</td>
									</tr>
								)
							})}

							{customDeductions && customDeductions?.length > 0 ? (
								<tr className={cx('deduction-tbody__custom')}>
									<td></td>
									<td className={cx('deduction-tbody__item--other')}>Other</td>
									<td></td>
									<td></td>
									<td></td>
								</tr>
							) : null}

							{customDeductions &&
								customDeductions?.map((item) => {
									if (item?.ytd === '' || item.ytd === '0.00') return null
									return (
										<tr key={item.name}>
											<td></td>
											<td className={cx('deduction-tbody__item')}>{item.name}</td>
											<td className={cx('deduction-tbody__item', 'ta-right')}>
												- {getStringValue(item.total)}
											</td>
											<td></td>
											<td className={cx('deduction-tbody__item', 'ta-right')}>{item.ytd}</td>
										</tr>
									)
								})}

							<tr>
								<td></td>
								<td className={cx('deduction-tbody__item--active')}>Net Pay</td>
								<td className={cx('deduction-tbody__item--active', 'ta-right')}>
									{'$'}
									{data?.items[activeItem].netpayTotal}
								</td>
								<td></td>
								<td></td>
							</tr>
							<tr>
								<td></td>
								<td className={cx('deduction-tbody__item--secondary')}>Check</td>
								<td className={cx('deduction-tbody__item--secondary', 'ta-right')}>
									- {data?.items[activeItem].netpayTotal}
								</td>
								<td></td>
								<td></td>
							</tr>
							<tr>
								<td></td>
								<td className={cx('deduction-tbody__item--active')}>Net Check</td>
								<td className={cx('deduction-tbody__item--active', 'ta-right')}>
									{'$'}
									{/* TODO: ask why there was always zero. Why in other templates we don't have this field? */}
									{/* {defaultValues.items[0].netCheck} */}
									{'0.00'}
								</td>
								<td></td>
								<td></td>
							</tr>
						</tbody>
					</table>
				</div>
				<div className={cx('main-right')}>
					<div className={cx('main-right__head')}>
						<div>
							<div
								className={cx(
									'main-right__title',
									data?.companyInfo.payrollLogo ? 'main-right__title--smaller' : '',
								)}>
								Earnings Statement
							</div>
							<table className={cx('main-right__payment-period')}>
								<tbody>
									<tr>
										<th>Period Beginning:</th>
										<td>{payPeriodFrom}</td>
									</tr>
									<tr>
										<th>Period Ending:</th>
										<td>{payPeriodTo}</td>
									</tr>
									<tr>
										<th>Pay Date:</th>
										<td>{payDate}</td>
									</tr>
								</tbody>
							</table>
						</div>

						{data?.companyInfo.payrollLogo && (
							<div className={cx('main-right__logo')}>
								{/*  eslint-disable-next-line @next/next/no-img-element */}
								<img src={data?.companyInfo.payrollLogo} alt="Payroll Logo" />
							</div>
						)}
					</div>

					<PaystubBaseMetrics
						name={data?.employeeInfo.employeeName}
						telephone={data?.employeeInfo.employeeTelephone || ''}
						address1={data?.employeeInfo.streetAddress1}
						address2={data?.employeeInfo.streetAddress2 || ''}
						city={data?.employeeInfo.city}
						state={data?.employeeInfo.state}
						zipCode={data?.employeeInfo.zipCode}
						className={cx('main-right__employee')}
					/>

					<table className={cx('main-right__other-info', 'other-info')}>
						<thead>
							<tr>
								<th className="other-info__head-item">Other Benefits and Information</th>
								<th className="other-info__head-item">This Period</th>
								<th className="other-info__head-item ta-right">Year To Date</th>
							</tr>
						</thead>
						<tbody>
							{data?.items[activeItem].customEarnings.map((item) => {
								if (!item) return null
								if (item?.ytd === '' || item.ytd === '0.00') return null
								return (
									<tr key={item.id}>
										<td> {camelCaseToString(item.name, 'upperFirst')}</td>
										<td>{item.total}</td>
										<td>{item.ytd}</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</main>

			<footer className={cx('pst1__footer', 'footer')}>
				<div className={cx('footer-left')}>
					{data?.companyInfo.employerLogo && (
						<div className={cx('footer-left__logo')}>
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src={data?.companyInfo.employerLogo} alt="Employer Logo" />
						</div>
					)}
				</div>

				<div className={cx('footer-right')}>
					<div className={cx('footer-right__top', 'footer-right-top')}>
						<div className={cx('footer-right-top__left')}>
							<PaystubBaseMetrics
								name={data?.companyInfo.employerName}
								telephone={data?.companyInfo.employerTelephone}
								address1={data?.companyInfo.streetAddress1}
								address2={data?.companyInfo.streetAddress2}
								city={data?.companyInfo.city}
								state={data?.companyInfo.state}
								zipCode={data?.companyInfo.zipCode}
								className="footer-right-top__about-company"
							/>
						</div>
						<div className={cx('footer-right-top__right')}>
							<PaystubAdviceInfo
								adviceNumber={data?.vchrNumber}
								employeeSocial={data?.employeeInfo.employeeSocial}
								paymentDay={payDate}
							/>
						</div>
					</div>

					<div className={cx('footer-right__middle', 'footer-right-middle')}>
						{!data?.employeeInfo.isDirectDeposit ? (
							<table className={cx('footer-right-middle__order-payment')}>
								<thead>
									<tr>
										<th className="fw-normal">Pay of the order of</th>
										<th>{data?.employeeInfo.employeeName}</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>This amount:</td>
										<td className="fw-bold">
											{getWordsFromNumber(getStringValue(data?.items[activeItem].netpayTotal))}
										</td>
										<td className="ta-right">
											{'$'}
											{getStringValue(data?.items[activeItem].netpayTotal)}
										</td>
									</tr>
								</tbody>
							</table>
						) : (
							<table className={cx('footer-right-middle__account-payment')}>
								<thead>
									<tr>
										<th>Deposited to the account of</th>
										<th>Account Number</th>
										<th>Transit</th>
										<th>ABA</th>
										<th className="ta-right">Amount</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>{data?.employeeInfo.employeeName}</td>
										<td>xxxxxxx{data?.employeeInfo.last4Digits}</td>
										<td>xxxx</td>
										<td>xxxx</td>
										<td className="ta-right">
											{'$'}
											{getStringValue(data?.items[activeItem].netpayTotal)}
										</td>
									</tr>
								</tbody>
							</table>
						)}
					</div>

					<div className={cx('footer-right__bottom', 'footer-right-bottom')}>
						<div className={cx('pst-non-negotiable', 'footer-right-bottom__non-negotiable')}>
							Non-negotiable
						</div>
					</div>
				</div>
			</footer>
		</div>
	)
}

PaystubTemplate1.displayName = 'PaystubTemplate1'

export const BackendPaystubTemplate1 = PaystubTemplate1

export default observer(PaystubTemplate1)
